import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "actionBar",
    "selectedCount",
    "item",
    "blockButton",
    "unblockButton",
    "deleteButton",
  ]

  static values = {
    target: String,
    selectionEnabled: { type: Boolean, default: true },
    longPressDuration: { type: Number, default: 450 },
    selectedCountTemplate: String,
  }

  connect() {
    this.selectedIds = new Set()
    this.longPressTimer = null
    this.longPressTriggered = false
    this.selectionActive = false

    if (!this.selectionEnabledValue) {
      return
    }

    this.updateActionBar()
  }

  startPress(event) {
    if (!this.selectionEnabledValue || event.button === 2) return

    const item = event.currentTarget
    this.longPressTriggered = false

    this.longPressTimer = window.setTimeout(() => {
      this.longPressTriggered = true
      this.activateSelection()
      this.toggleItem(item)
    }, this.longPressDurationValue)
  }

  endPress() {
    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer)
      this.longPressTimer = null
    }
  }

  cancelPress() {
    this.endPress()
  }

  toggleSelection(event) {
    if (!this.selectionEnabledValue) return

      // Ignore clicks on interactive elements (buttons, links, dropdowns, etc.)
      const target = event.target
      const item = event.currentTarget

      // Check if click originated from an interactive element
      const isInteractiveElement = target.closest('button, a, [data-controller*="dropdown"], [data-controller*="contact-invite"], [data-dropdown-target], form')

      // Also check if the click is within the dropdown menu area
      const isInDropdownMenu = target.closest('[data-dropdown-target="menu"]')

      if (isInteractiveElement || isInDropdownMenu) {
          return
      }

    if (this.longPressTriggered) {
      event.preventDefault()
      event.stopPropagation()
      this.longPressTriggered = false
      return
    }

    if (!this.selectionActive) return

    event.preventDefault()
    event.stopPropagation()

      this.toggleItem(item)
  }

    stopPropagation(event) {
        event.stopPropagation()
  }

  clearSelection() {
    if (!this.selectionEnabledValue) return

    this.selectedIds.clear()
    this.itemTargets.forEach((item) => {
      item.classList.remove("bg-gray-50", "dark:bg-neutral-800")
      const indicator = item.querySelector("[data-selection-indicator]")
      if (indicator) {
        indicator.classList.add("hidden")
      }
    })

    this.updateActionBar()
  }

  submitBlock() {
    this.submitAction("block", this.blockButtonTarget?.dataset.confirm)
  }

  submitUnblock() {
    this.submitAction("unblock", this.unblockButtonTarget?.dataset.confirm)
  }

  submitDelete() {
    this.submitAction("delete", this.deleteButtonTarget?.dataset.confirm)
  }

  submitAction(action, confirmationMessage) {
    if (!this.selectionEnabledValue || this.selectedIds.size === 0) return

    if (confirmationMessage && !window.confirm(confirmationMessage)) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const payload = {
      target: this.targetValue,
      bulk_action: action,
      ids: Array.from(this.selectedIds),
    }

    fetch("/people/bulk_action", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Bulk action failed")
        }
        return response.json()
      })
      .then(() => {
        window.location.reload()
      })
      .catch(() => {
        window.alert("Une erreur est survenue. Merci de réessayer.")
      })
  }

  toggleItem(item) {
    const id = item.dataset.personId
    if (!id) return

    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id)
      item.classList.remove("bg-gray-50", "dark:bg-neutral-800")
      const indicator = item.querySelector("[data-selection-indicator]")
      if (indicator) {
        indicator.classList.add("hidden")
      }
    } else {
      this.selectedIds.add(id)
      item.classList.add("bg-gray-50", "dark:bg-neutral-800")
      const indicator = item.querySelector("[data-selection-indicator]")
      if (indicator) {
        indicator.classList.remove("hidden")
      }
    }

    this.updateActionBar()
  }

  updateActionBar() {
    if (!this.selectionEnabledValue) return

    const count = this.selectedIds.size

    if (count === 0) {
      this.deactivateSelection()
      return
    }

    this.activateSelection()

    if (this.hasSelectedCountTarget) {
      const template = this.hasSelectedCountTemplateValue
        ? this.selectedCountTemplateValue
        : "%{count} sélectionné(s)"
      this.selectedCountTarget.textContent = template.replace("%{count}", count)
    }

    // Check if ANY selected item is blocked
    const selectedItems = this.itemTargets.filter((item) =>
      this.selectedIds.has(item.dataset.personId)
    )

    const hasBlocked = selectedItems.some((item) => item.dataset.blocked === "true")

    // Mutually exclusive: Block visible when NO blocked items, Unblock visible when ANY blocked
    // Using style.display instead of hidden class due to CSS specificity (inline-flex overrides hidden)
    if (this.hasBlockButtonTarget) {
      this.blockButtonTarget.style.display = hasBlocked ? "none" : ""
    }

    if (this.hasUnblockButtonTarget) {
      this.unblockButtonTarget.style.display = hasBlocked ? "" : "none"
    }
  }

  activateSelection() {
    if (this.selectionActive) return

    this.selectionActive = true
    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.remove("hidden")
    }

    const header = document.querySelector("main > header")
    if (header) {
      header.classList.add("hidden")
    }
  }

  deactivateSelection() {
    if (!this.selectionActive) return

    this.selectionActive = false
    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.add("hidden")
    }

    const header = document.querySelector("main > header")
    if (header) {
      header.classList.remove("hidden")
    }
  }
}
